import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Upload, Loader2, X, ExternalLink, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileSrc, formatApiError, uploadImage } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { RichEditor } from "./RichEditor";

const field = "w-full rounded-xl border border-white/10 bg-ink px-4 py-3 text-paper outline-none transition-colors focus:border-volt";
const EMPTY = { title: "", slug: "", excerpt: "", cover_url: "", cover_alt: "", category: "", seo_title: "", seo_description: "", content: "", published: true };
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const Counter = ({ value, max, ideal }) => {
  const n = (value || "").length;
  const color = n > max ? "text-red-400" : n >= ideal ? "text-lime" : "text-zinc-400";
  return <span className={`text-xs ${color}`}>{n}/{max}</span>;
};

const Label = ({ children, right }) => (
  <span className="mb-1.5 flex items-center justify-between text-sm text-zinc-300"><span>{children}</span>{right}</span>
);

const CoverField = ({ form, setForm }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, cover_url: url, cover_alt: f.cover_alt || f.title }));
      toast.success("Cover diupload");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <Label>Gambar cover (disarankan rasio 16:9, mis. 1600×900)</Label>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden rounded-xl bg-surface ring-1 ring-white/10 sm:w-64" data-testid="admin-article-cover-preview">
          {form.cover_url ? <img src={fileSrc(form.cover_url)} alt="" className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center text-xs text-zinc-500">Belum ada cover</span>}
          {form.cover_url ? <button type="button" onClick={() => setForm((f) => ({ ...f, cover_url: "" }))} data-testid="admin-article-cover-remove" aria-label="Hapus cover" className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-ink/80 text-paper hover:text-red-400"><X className="h-4 w-4" /></button> : null}
        </div>
        <div className="flex-1 space-y-3">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="admin-article-cover-upload" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-zinc-200 hover:border-volt disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form.cover_url ? "Ganti cover" : "Upload cover"}
          </button>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="admin-article-cover-input" />
          <input data-testid="admin-article-cover-alt" placeholder="Deskripsi gambar untuk SEO (alt text)" value={form.cover_alt} onChange={(e) => setForm((f) => ({ ...f, cover_alt: e.target.value }))} maxLength={160} className={field} />
          <p className="text-xs text-zinc-400">Gambar otomatis dikompres agar ringan.</p>
        </div>
      </div>
    </div>
  );
};

const SeoFields = ({ form, setForm }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const title = form.seo_title || form.title || "Judul artikel";
  const desc = form.seo_description || form.excerpt || "Deskripsi artikel akan tampil di sini.";
  const slug = form.slug || slugify(form.title) || "judul-artikel";
  return (
    <div className="space-y-4 rounded-2xl border border-white/10 p-4" data-testid="admin-article-seo">
      <p className="font-display text-paper">SEO Google</p>
      <label className="block"><Label right={<Counter value={form.seo_title} max={70} ideal={45} />}>Judul SEO (ideal 50–60 karakter, kosongkan = pakai judul)</Label>
        <input data-testid="admin-article-seo-title" value={form.seo_title} onChange={set("seo_title")} maxLength={70} className={field} /></label>
      <label className="block"><Label right={<Counter value={form.seo_description} max={170} ideal={120} />}>Deskripsi SEO (ideal 140–160 karakter, kosongkan = pakai ringkasan)</Label>
        <textarea data-testid="admin-article-seo-description" rows={2} value={form.seo_description} onChange={set("seo_description")} maxLength={170} className={field} /></label>
      <label className="block"><Label>Alamat artikel (URL)</Label>
        <div className="flex items-center overflow-hidden rounded-xl border border-white/10 bg-ink focus-within:border-volt">
          <span className="hidden shrink-0 pl-4 text-sm text-zinc-500 sm:block">/blog/</span>
          <input data-testid="admin-article-slug" value={form.slug} placeholder={slugify(form.title)} onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))} className="w-full bg-transparent px-4 py-3 text-paper outline-none sm:pl-1" />
        </div>
        {form.id ? <span className="mt-1 block text-xs text-amber-300">Jangan ganti URL artikel yang sudah terbit, nanti link lama di Google jadi rusak.</span> : null}
      </label>
      <div className="rounded-xl bg-white p-4" data-testid="admin-article-google-preview">
        <p className="text-xs text-[#202124]">bagipulsa.com › blog › {slug}</p>
        <p className="mt-1 line-clamp-1 text-lg text-[#1a0dab]">{title.length > 60 ? `${title.slice(0, 60)}…` : title} | BagiPulsa</p>
        <p className="mt-1 line-clamp-2 text-sm text-[#4d5156]">{desc.length > 160 ? `${desc.slice(0, 160)}…` : desc}</p>
      </div>
    </div>
  );
};

export const ArticleForm = ({ initial, categories, onDone, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    const { id, created_at, updated_at, related, ...payload } = form;
    try {
      if (id) await api.put(`/admin/articles/${id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success("Artikel disimpan");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <form onSubmit={save} data-testid="admin-article-form" className="space-y-5">
      <label className="block"><Label right={<Counter value={form.title} max={160} ideal={30} />}>Judul artikel</Label>
        <input data-testid="admin-article-title" required minLength={3} maxLength={160} value={form.title} onChange={set("title")} className={field} /></label>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="block"><Label>Kategori</Label>
          <input data-testid="admin-article-category" list="article-categories" placeholder="mis. Tips, Promo, Berita" maxLength={40} value={form.category} onChange={set("category")} className={field} />
          <datalist id="article-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist></label>
        <label className="block"><Label right={<Counter value={form.excerpt} max={300} ideal={80} />}>Ringkasan singkat</Label>
          <input data-testid="admin-article-excerpt" maxLength={300} value={form.excerpt} onChange={set("excerpt")} className={field} /></label>
      </div>
      <CoverField form={form} setForm={setForm} />
      <div><Label>Isi artikel</Label><RichEditor value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} /></div>
      <SeoFields form={form} setForm={setForm} />
      <label className="flex items-center gap-2 text-sm text-zinc-300">
        <Switch data-testid="admin-article-published" checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} className="data-[state=checked]:bg-volt data-[state=unchecked]:!bg-zinc-700" />
        {form.published ? "Terbit (tampil di website)" : "Draft (belum tampil)"}
      </label>
      <div className="flex flex-wrap gap-3">
        <button data-testid="admin-article-save" className="rounded-full bg-volt px-6 py-2.5 font-semibold text-ink">Simpan</button>
        <button type="button" data-testid="admin-article-cancel" onClick={onCancel} className="rounded-full border border-white/15 px-6 py-2.5 text-zinc-200">Batal</button>
      </div>
    </form>
  );
};

const PER_PAGE = 10;
const iconBtn = "grid h-9 w-9 place-items-center rounded-full border border-white/15";

const ArticleRow = ({ a, onRemove }) => (
  <div data-testid={`admin-article-row-${a.slug}`} className="flex items-center justify-between gap-3 border-b border-white/[0.07] py-4">
    <Link to={`/tigasatu/artikel/${a.id}`} className="flex min-w-0 items-center gap-3">
      <div className="hidden h-12 w-20 shrink-0 overflow-hidden rounded-lg bg-ink sm:block">{a.cover_url ? <img src={fileSrc(a.cover_url)} alt="" className="h-full w-full object-cover" /> : null}</div>
      <div className="min-w-0">
        <p className="truncate font-display text-paper">{a.title}</p>
        <p className="truncate text-xs text-zinc-400"><span className={a.published ? "text-lime" : "text-amber-300"}>{a.published ? "Terbit" : "Draft"}</span>{a.category ? ` · ${a.category}` : ""} · {new Date(a.created_at).toLocaleDateString("id-ID")}</p>
      </div>
    </Link>
    <div className="flex shrink-0 gap-2">
      {a.published ? <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-article-view-${a.slug}`} className={`${iconBtn} hover:border-volt`} aria-label="Lihat"><ExternalLink className="h-4 w-4" /></a> : null}
      <Link to={`/tigasatu/artikel/${a.id}`} data-testid={`admin-article-edit-${a.slug}`} className={`${iconBtn} hover:border-volt`} aria-label="Edit"><Pencil className="h-4 w-4" /></Link>
      <button data-testid={`admin-article-delete-${a.slug}`} onClick={() => onRemove(a)} className={`${iconBtn} hover:border-red-400`} aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export const ArticlesAdmin = () => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const load = () => api.get("/admin/articles").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);
  const remove = async (a) => {
    if (!window.confirm(`Hapus "${a.title}"?`)) return;
    await api.delete(`/admin/articles/${a.id}`).catch(() => {});
    toast.success("Artikel dihapus");
    load();
  };
  const list = (items || []).filter((a) => (status === "all" || (status === "pub") === a.published) && a.title.toLowerCase().includes(q.toLowerCase()));
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const cur = Math.min(page, pages);
  const shown = list.slice((cur - 1) * PER_PAGE, cur * PER_PAGE);
  return (
    <div data-testid="admin-articles-panel" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Link to="/tigasatu/artikel/baru" data-testid="admin-article-new" className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-volt px-5 py-2.5 text-sm font-semibold text-ink"><Plus className="h-4 w-4" /> Artikel baru</Link>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input data-testid="admin-article-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cari judul artikel..." className="w-full rounded-full border border-white/10 bg-ink py-2.5 pl-11 pr-4 text-sm text-paper outline-none focus:border-volt" />
        </div>
        <div className="flex gap-1 self-start rounded-full border border-white/10 p-1 sm:self-auto" data-testid="admin-article-status-filter">
          {[["all", "Semua"], ["pub", "Terbit"], ["draft", "Draft"]].map(([k, l]) => (
            <button key={k} onClick={() => { setStatus(k); setPage(1); }} data-testid={`admin-article-filter-${k}`} className={`rounded-full px-3 py-1.5 text-xs ${status === k ? "bg-volt font-semibold text-ink" : "text-zinc-300"}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-zinc-400" data-testid="admin-article-count">{list.length} artikel</p>
      {items && !shown.length ? <p className="py-6 text-sm text-zinc-400">Tidak ada artikel.</p> : null}
      {shown.map((a) => <ArticleRow key={a.id} a={a} onRemove={remove} />)}
      {pages > 1 ? (
        <div className="flex items-center justify-center gap-3 pt-4" data-testid="admin-article-pagination">
          <button disabled={cur <= 1} onClick={() => setPage(cur - 1)} data-testid="admin-article-prev" aria-label="Sebelumnya" className={`${iconBtn} disabled:opacity-30`}><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm text-zinc-300">Halaman {cur} dari {pages}</span>
          <button disabled={cur >= pages} onClick={() => setPage(cur + 1)} data-testid="admin-article-next" aria-label="Berikutnya" className={`${iconBtn} disabled:opacity-30`}><ChevronRight className="h-4 w-4" /></button>
        </div>
      ) : null}
    </div>
  );
};
